import React, { useState } from 'react'
import { ArrowLeft, ArrowRight, Loader2, MessageSquare, Send, X } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import type { DeliverableItem } from '@adthlete/shared-frontend'
import { useGetAthleteScoutingQuery } from '@/redux/marketerApi'
import { DELIVERABLES, INCLUDED_LICENSE_DAYS } from '@adthlete/common/utils/dealPricing'
import { Athlete } from './AthleteCard'
import { BookingPicker } from './BookingPicker'
import {
  DELIVERABLE_FORMAT,
  bookingFor,
  bookingTotal,
  emptyBooking,
  type Booking,
} from './booking'
import type { BookPick } from '@adthlete/shared-frontend'

interface SendOfferModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  athlete: Athlete | null
  campaignName?: string
  defaultAmount?: string
  onSubmit: (data: OfferData) => void
  isSending?: boolean
  /** Shown above the message, e.g. a campaign picker */
  beforeMessage?: React.ReactNode
  /** The form's title (default "Work with <first name>") */
  title?: string
  /** Under the title, e.g. "Fall Hydration · HydroX" */
  subtitle?: string | null
  /** Send the message on its own (step 1). Without it, onSubmit gets the message with no offer. */
  onSendMessage?: (message: string) => void
  /** Replaces the school and sport line under the athlete's name; null hides it */
  athleteSubtitle?: string | null
  /** Hide "Send message", for when the brand is already in a conversation with the athlete */
  offerOnly?: boolean
  /** Opened from "Book this" on the storefront: what they picked, filled in */
  pick?: BookPick | null
}

export interface OfferData {
  paymentType: 'fixed' | 'custom'
  amount: number | null
  customPaymentTerms: string
  dueDate: string
  message: string
  deliverables: DeliverableItem[]
  licensing?: {
    enabled: boolean
    platforms: string[]
    territory: string
    term: string
    fee?: number
  }
  /** What the brand is booking: a rate-card deliverable in one of their themes, or custom */
  booking?:
    | {
        kind: 'rate_card'
        theme: { id: string; label: string; category: string } | null
        deliverable: string
        quantity: number
        unitPrice: number
        licenseDays: number | null
        licensePrice: number | null
      }
    | { kind: 'custom'; description: string; askForRate: boolean }
}

export const SendOfferModal: React.FC<SendOfferModalProps> = ({
  open,
  onOpenChange,
  athlete,
  campaignName,
  defaultAmount = '',
  onSubmit,
  isSending = false,
  beforeMessage,
  offerOnly = false,
  title,
  subtitle,
  onSendMessage,
  athleteSubtitle,
  pick = null,
}) => {
  const [step, setStep] = useState<1 | 2>(1)

  // Step 1 fields
  const [message, setMessage] = useState('')
  const [dueDate, setDueDate] = useState('')

  // Step 2: what they're booking, priced from the athlete's rate card
  const { data: scouting, isFetching: loadingCard } = useGetAthleteScoutingQuery(
    athlete?.id ?? '',
    { skip: !open || !athlete?.id },
  )
  const rateCard = scouting?.rateCard ?? null
  const [booking, setBooking] = useState<Booking>(() => emptyBooking(null))

  // What they picked on the storefront, in words: "a TikTok, Day in the life"
  const pickTheme = pick?.themeId
    ? rateCard?.themes.find((t) => t.id === pick.themeId)?.label
    : undefined
  const pickItem =
    pick?.kind === 'item' ? rateCard?.items?.find((i) => i.id === pick.itemId) : undefined
  const pickFormat =
    pick?.kind === 'rate_card'
      ? DELIVERABLES.find((d) => d.id === pick.deliverable)?.label
      : pickItem
        ? pickItem.label
        : 'something custom'
  const pickMessage = (name: string) =>
    `Hi ${name.split(' ')[0]}, we'd love to book ${pickFormat ? `${/^[aeiou]/i.test(pickFormat) ? 'an' : 'a'} ${pickFormat}` : 'a post'}${
      pickTheme ? ` in your "${pickTheme}" style` : ''
    }. Here's what we have in mind: `

  // Whether the brand has typed in the message, so a late rate card doesn't overwrite it
  const typed = React.useRef(false)

  const reset = () => {
    typed.current = false
    setStep(1)
    setMessage(pick && athlete ? pickMessage(athlete.name) : '')
    setDueDate('')
    setBooking(pick ? bookingFor(rateCard, pick) : emptyBooking(rateCard))
  }

  React.useEffect(() => {
    if (open) reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, athlete, pick])

  // The rate card arrives after the form opens; start from it (and the pick) once it does
  React.useEffect(() => {
    if (!open || !rateCard) return
    setBooking(pick ? bookingFor(rateCard, pick) : emptyBooking(rateCard))
    if (pick && athlete && !typed.current) setMessage(pickMessage(athlete.name))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rateCard, pick])

  const buildOfferData = (withOffer: boolean): OfferData => {
    const base = { customPaymentTerms: '', dueDate, message }
    if (!withOffer) {
      return { ...base, paymentType: 'fixed', amount: null, deliverables: [] }
    }
    if (booking.kind === 'custom') {
      return {
        ...base,
        paymentType: 'fixed',
        amount: booking.askForRate ? null : Number(booking.customAmount) || null,
        deliverables: [],
        // The included 30 days of paid usage comes with custom requests too
        licensing: {
          enabled: true,
          platforms: ['instagram', 'tiktok'],
          territory: 'United States',
          term: String(INCLUDED_LICENSE_DAYS),
          fee: 0,
        },
        booking: {
          kind: 'custom',
          description: booking.description.trim(),
          askForRate: booking.askForRate,
        },
      }
    }
    const { platform, format } = DELIVERABLE_FORMAT[booking.deliverable]
    const theme = rateCard?.themes?.find((t) => t.id === booking.themeId) ?? null
    const licensePrice =
      booking.licenseDays && rateCard ? rateCard.licensing[booking.licenseDays].amount : null
    const count = booking.deliverable === 'appearance' ? 1 : booking.quantity
    const deliverables: DeliverableItem[] = Array.from({ length: count }, (_, i) => ({
      id: `${booking.deliverable}-${i}-${Date.now()}`,
      platform,
      format,
      notes: theme ? theme.label : null,
      order: i,
      dueDate: dueDate || null,
      sameAsDeliverableId: null,
    }))
    return {
      ...base,
      paymentType: 'fixed',
      amount: bookingTotal(booking, rateCard),
      deliverables,
      // Every deal includes 30 days of paid usage; an extension adds to it at their price
      licensing:
        booking.deliverable === 'appearance'
          ? undefined
          : {
              enabled: true,
              platforms: [platform === 'TikTok' ? 'tiktok' : 'instagram'],
              territory: 'United States',
              term: String(INCLUDED_LICENSE_DAYS + (booking.licenseDays ?? 0)),
              fee: (licensePrice ?? 0) * booking.quantity,
            },
      booking: {
        kind: 'rate_card',
        theme,
        deliverable: booking.deliverable,
        quantity: booking.quantity,
        unitPrice: Number(booking.unitPrice) || 0,
        licenseDays: booking.licenseDays,
        licensePrice,
      },
    }
  }

  // What's missing before the offer can go
  const bookingProblem =
    booking.kind === 'custom'
      ? booking.description.trim().length < 3
        ? 'Describe what you want made'
        : !booking.askForRate && !(Number(booking.customAmount) > 0)
          ? 'Add your offer, or ask for their rate'
          : null
      : !(Number(booking.unitPrice) > 0)
        ? 'Add a price'
        : null

  if (!athlete) return null

  const first = athlete.name.split(' ')[0]
  const asking = step === 2 && booking.kind === 'custom' && booking.askForRate
  const strip =
    athleteSubtitle !== null
      ? (athleteSubtitle ??
        [athlete.school || 'Independent', athlete.sports?.[0]].filter(Boolean).join(' · '))
      : null
  const sendMessageOnly = () =>
    onSendMessage ? onSendMessage(message.trim()) : onSubmit(buildOfferData(false))

  const StepPill: React.FC<{ n: 1 | 2; label: string }> = ({ n, label }) => (
    <span className="flex items-center gap-2">
      <span
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-full border-2 text-sm font-semibold',
          step === n
            ? 'border-[#9BD85A] bg-[#C0FD72] text-[#0F172A]'
            : 'border-border bg-card text-muted-foreground',
        )}
      >
        {n}
      </span>
      <span
        className={cn(
          'text-sm',
          step === n ? 'font-semibold text-foreground' : 'text-muted-foreground',
        )}
      >
        {label}
      </span>
    </span>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="max-h-[94vh] gap-0 overflow-y-auto border-border bg-card p-0 text-foreground sm:max-w-[700px] sm:rounded-3xl"
      >
        {/* Title */}
        <div className="flex items-start gap-4 border-b border-border px-5 py-5 sm:px-6">
          <button
            type="button"
            onClick={() => (step === 2 ? setStep(1) : onOpenChange(false))}
            aria-label={step === 2 ? 'Back to message' : 'Close'}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border text-foreground hover:bg-muted"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-2xl font-bold text-foreground">
              {title ?? (pick ? `Book ${first}'s content` : `Work with ${first}`)}
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground">
              {subtitle ?? campaignName ?? 'Send a message or an offer'}
            </DialogDescription>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
            className="rounded-md p-1.5 text-muted-foreground hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Who, and which step */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border px-5 py-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            {athlete.avatar ? (
              <img
                src={athlete.avatar}
                alt=""
                className="h-14 w-14 shrink-0 rounded-full object-cover"
              />
            ) : (
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-muted text-lg font-semibold text-foreground">
                {athlete.name.charAt(0)}
              </span>
            )}
            <div className="min-w-0">
              <p className="truncate text-lg font-semibold text-foreground">{athlete.name}</p>
              {strip ? <p className="truncate text-muted-foreground">{strip}</p> : null}
            </div>
          </div>
          <div className="w-full sm:w-auto">
            <div className="flex items-center gap-3">
              <StepPill n={1} label="Message" />
              <span className="h-px w-10 bg-border" />
              <StepPill n={2} label="Booking details" />
            </div>
            <div className="mt-2 grid grid-cols-2 gap-0.5">
              <span className={cn('h-0.5', step === 1 ? 'bg-[#84CC16]' : 'bg-border')} />
              <span className={cn('h-0.5', step === 2 ? 'bg-[#84CC16]' : 'bg-border')} />
            </div>
          </div>
        </div>

        {step === 1 ? (
          <>
            <div className="space-y-4 px-5 py-5 sm:px-6">
              {pick ? (
                <div className="rounded-2xl border border-[#9BD85A] bg-[#F7FEE7] px-4 py-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#3F7A0A]">
                    You're booking
                  </p>
                  <p className="mt-0.5 font-semibold text-foreground">
                    {pick.kind === 'rate_card'
                      ? pickFormat
                      : pickItem
                        ? pickItem.label
                        : 'Something custom'}
                    {pickTheme ? ` · ${pickTheme}` : ''}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {booking.kind === 'rate_card' && Number(booking.unitPrice) > 0
                      ? `$${Number(booking.unitPrice).toLocaleString()} each, ${INCLUDED_LICENSE_DAYS} days of ad usage included. Adjust it on the next step.`
                      : pickItem
                        ? `$${Number(booking.customAmount || 0).toLocaleString()} (${pickItem.unit}). Adjust it on the next step.`
                        : 'Describe it and set your price on the next step.'}
                  </p>
                </div>
              ) : null}
              {beforeMessage}
              <div>
                <label htmlFor="offerMessage" className="mb-2 block font-semibold text-foreground">
                  Message
                </label>
                <textarea
                  id="offerMessage"
                  rows={6}
                  value={message}
                  onChange={(e) => {
                    typed.current = true
                    setMessage(e.target.value)
                  }}
                  placeholder={`Hi ${first}, we love your content and would like to work together on…`}
                  className="w-full resize-none rounded-xl border border-border bg-card px-4 py-3 text-base text-foreground outline-none placeholder:text-muted-foreground focus:border-[#84CC16] focus:ring-4 focus:ring-[#C0FD72]/40"
                />
                <p className="mt-2 text-sm text-muted-foreground">
                  Say who you are and what you have in mind. Next, add the offer itself.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4 sm:px-6">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="h-11 rounded-xl border border-border px-5 font-medium text-foreground hover:bg-muted"
              >
                Cancel
              </button>
              <div className="flex flex-wrap gap-2">
                {!offerOnly ? (
                  <button
                    type="button"
                    onClick={sendMessageOnly}
                    disabled={isSending || !message.trim()}
                    className="inline-flex h-11 items-center gap-2 rounded-xl border border-border px-5 font-medium text-foreground hover:bg-muted disabled:opacity-50"
                  >
                    {isSending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <MessageSquare className="h-4 w-4" />
                    )}
                    Send message only
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="inline-flex h-11 items-center gap-2 rounded-xl bg-[#C0FD72] px-5 font-semibold text-[#0F172A] hover:bg-[#B2F25E]"
                >
                  Booking details <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="px-5 py-5 sm:px-6">
              <BookingPicker
                value={booking}
                onChange={setBooking}
                rateCard={rateCard}
                athleteFirstName={first}
                dueDate={dueDate}
                onDueDate={setDueDate}
              />
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-border px-5 py-4 sm:px-6">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="inline-flex h-12 items-center gap-2 rounded-xl border border-border px-5 font-medium text-foreground hover:bg-muted"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <button
                type="button"
                onClick={() => onSubmit(buildOfferData(true))}
                disabled={isSending || loadingCard || !!bookingProblem}
                title={bookingProblem ?? undefined}
                className="inline-flex h-12 items-center gap-2 rounded-xl bg-[#C0FD72] px-6 font-semibold text-[#0F172A] hover:bg-[#B2F25E] disabled:opacity-50"
              >
                {isSending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {asking ? 'Request quote' : 'Send booking request'}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
